import { GridLayoutDefinition, GridLayoutInfo, log } from '@livekit/components-core'

export const GRID_LAYOUTS: GridLayoutDefinition[] = [
  // Mobile portrait
  {
    columns: 2,
    rows: 4,
    orientation: 'portrait',
    minWidth: 0, // Для мобильных не ставим ограничений
    minHeight: 0,
  },
  // Mobile landscape
  {
    columns: 4,
    rows: 2,
    orientation: 'landscape',
    minWidth: 0,
    minHeight: 0,
  },
  // Desktop и остальные варианты, как было раньше
  {
    columns: 1,
    rows: 1,
  },
  {
    columns: 1,
    rows: 2,
    orientation: 'portrait',
  },
  {
    columns: 2,
    rows: 1,
    orientation: 'landscape',
  },
  {
    columns: 2,
    rows: 2,
    minWidth: 560,
  },
  {
    columns: 3,
    rows: 3,
    minWidth: 700,
  },
  {
    columns: 4,
    rows: 4,
    minWidth: 960,
  },
  {
    columns: 5,
    rows: 5,
    minWidth: 1100,
  },
] as const

/**
 * @internal
 */
export function expandAndSortLayoutDefinitions(layouts: GridLayoutDefinition[]): GridLayoutInfo[] {
  return [...layouts]
    .map((layout) => {
      return {
        name: `${layout.columns}x${layout.rows}`,
        columns: layout.columns,
        rows: layout.rows,
        maxTiles: layout.columns * layout.rows,
        minWidth: layout.minWidth ?? 0,
        minHeight: layout.minHeight ?? 0,
        orientation: layout.orientation,
      } satisfies GridLayoutInfo
    })
    .sort((a, b) => {
      if (a.maxTiles !== b.maxTiles) {
        return a.maxTiles - b.maxTiles
      } else if (a.minWidth !== 0 || b.minWidth !== 0) {
        return a.minWidth - b.minWidth
      } else if (a.minHeight !== 0 || b.minHeight !== 0) {
        return a.minHeight - b.minHeight
      } else {
        return 0
      }
    })
}

export function selectGridLayoutMobile(
  participantCount: number,
  width: number,
  height: number,
): GridLayoutInfo {
  const orientation = width > height ? 'landscape' : 'portrait'

  let rows = 1
  let columns = 1
  let name = '1x1'

  if (participantCount === 1) {
    rows = 1
    columns = 1
  } else if (participantCount === 2) {
    if (orientation === 'portrait') {
      rows = 2
      columns = 1
    } else {
      rows = 1
      columns = 2
    }
  } else if (participantCount >= 3 && participantCount <= 4) {
    rows = 2
    columns = 2
  } else if (participantCount >= 5 && participantCount <= 6) {
    if (orientation === 'portrait') {
      rows = 3
      columns = 2
    } else {
      rows = 2
      columns = 3
    }
  } else if (participantCount >= 7) {
    if (orientation === 'portrait') {
      rows = 4
      columns = 2
    } else {
      rows = 2
      columns = 4
    }
  }

  name = `${columns}x${rows}`

  return {
    name,
    columns,
    rows,
    maxTiles: columns * rows,
    minWidth: 0,
    minHeight: 0,
    orientation,
  }
}

export function selectGridLayout(
  layoutDefinitions: GridLayoutDefinition[],
  participantCount: number,
  width: number,
  height: number,
): GridLayoutInfo {
  if (layoutDefinitions.length < 1) {
    throw new Error('At least one grid layout definition must be provided.')
  }
  const layouts = expandAndSortLayoutDefinitions(layoutDefinitions)
  if (width <= 0 || height <= 0) {
    return layouts[0]
  }

  // Определяем устройство как "мобильное", если ширина экрана <= 600px
  const isMobile = Math.min(width, height) <= 600
  const containerOrientation = width > height ? 'landscape' : 'portrait'

  // Если мобильное устройство, отбираем только нужные layout-ы
  if (isMobile) {
    return selectGridLayoutMobile(participantCount, width, height)
  }

  if (participantCount >= 5 && participantCount <= 6 && containerOrientation === 'landscape') {
    return {
      name: '3x2',
      columns: 3,
      rows: 2,
      maxTiles: 3 * 2,
      minWidth: 0,
      minHeight: 0,
      orientation: 'landscape',
    }
  }

  if (participantCount >= 7 && participantCount <= 9 && containerOrientation === 'landscape') {
    return {
      name: '3x3',
      columns: 3,
      rows: 3,
      maxTiles: 3 * 3,
      minWidth: 0,
      minHeight: 0,
      orientation: 'landscape',
    }
  }

  if (participantCount >= 5 && participantCount <= 6 && containerOrientation === 'portrait') {
    return {
      name: '2x3',
      columns: 2,
      rows: 3,
      maxTiles: 2 * 3,
      minWidth: 0,
      minHeight: 0,
      orientation: 'portrait',
    }
  }

  if (participantCount >= 7 && containerOrientation === 'portrait') {
    return {
      name: '2x4',
      columns: 2,
      rows: 4,
      maxTiles: 2 * 4,
      minWidth: 0,
      minHeight: 0,
      orientation: 'portrait',
    }
  }

  if (participantCount >= 10 && participantCount <= 12) {
    return {
      name: '4x3',
      columns: 4,
      rows: 3,
      maxTiles: 4 * 3,
      minWidth: 0,
      minHeight: 0,
      orientation: width > height ? 'landscape' : 'portrait',
    }
  }
  if (participantCount >= 13 && participantCount <= 16) {
    return {
      name: '4x4',
      columns: 4,
      rows: 4,
      maxTiles: 4 * 4,
      minWidth: 0,
      minHeight: 0,
      orientation: width > height ? 'landscape' : 'portrait',
    }
  }
  if (participantCount >= 15 && participantCount <= 20) {
    return {
      name: '5x4',
      columns: 5,
      rows: 4,
      maxTiles: 5 * 4,
      minWidth: 0,
      minHeight: 0,
      orientation: width > height ? 'landscape' : 'portrait',
    }
  }
  if (participantCount >= 21) {
    return {
      name: '5x5',
      columns: 5,
      rows: 5,
      maxTiles: 5 * 5,
      minWidth: 0,
      minHeight: 0,
      orientation: width > height ? 'landscape' : 'portrait',
    }
  }

  // Обычная логика выбора layout (для остальных случаев)
  let currentLayoutIndex = 0
  let layout = layouts.find((layout_, index, allLayouts) => {
    currentLayoutIndex = index
    const isBiggerLayoutAvailable =
      allLayouts.findIndex((l, i) => {
        const fitsOrientation = !l.orientation || l.orientation === containerOrientation
        const layoutIsBiggerThanCurrent = i > index
        const layoutFitsSameAmountOfParticipants = l.maxTiles === layout_.maxTiles
        return layoutIsBiggerThanCurrent && layoutFitsSameAmountOfParticipants && fitsOrientation
      }) !== -1
    return layout_.maxTiles >= participantCount && !isBiggerLayoutAvailable
  })

  if (layout === undefined) {
    layout = layouts[layouts.length - 1]
    if (layout) {
      log.warn(
        `No layout found for: participantCount: ${participantCount}, width/height: ${width}/${height} fallback to biggest available layout (${layout}).`,
      )
    } else {
      throw new Error(`No layout or fallback layout found.`)
    }
  }
  if (width < layout.minWidth || height < layout.minHeight) {
    if (currentLayoutIndex > 0) {
      const smallerLayout = layouts[currentLayoutIndex - 1]
      layout = selectGridLayout(
        layouts.slice(0, currentLayoutIndex),
        smallerLayout.maxTiles,
        width,
        height,
      )
    }
  }
  return layout
}
